import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Zap, Search, Loader2, Settings, Globe, ArrowUpRight, ArrowDownLeft, RefreshCw, CheckCircle, XCircle, Clock, Shield } from 'lucide-react';
import { formatCurrency, formatDateShort, formatStatutFiscal, getStatutFiscalColor, FORMAT_EINVOICE_LABELS } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function EInvoicing() {
  const [tab, setTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [ereportingStats, setEreportingStats] = useState(null);
  const [ereportingDash, setEreportingDash] = useState(null);
  const [annuaireSearch, setAnnuaireSearch] = useState('');
  const [annuaireResult, setAnnuaireResult] = useState(null);
  const [annuaireLoading, setAnnuaireLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [configForm, setConfigForm] = useState({ is_active: false, pdp_provider: '', pdp_api_url: '', pdp_api_key: '', siren: '', format_prefere: 'factur-x', environment: 'sandbox' });
  const [savingConfig, setSavingConfig] = useState(false);
  const [directionFilter, setDirectionFilter] = useState('all');
  const [statutFilter, setStatutFilter] = useState('all');
  const [statuts, setStatuts] = useState(null);

  useEffect(() => { loadData(); }, []);
  useEffect(() => { if (tab === 'transactions') loadTransactions(); }, [tab, directionFilter, statutFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [dashRes, configRes, statutsRes, erDash] = await Promise.all([
        axios.get(`${API_URL}/pa/dashboard`).catch(() => ({ data: null })),
        axios.get(`${API_URL}/pa/config`).catch(() => ({ data: null })),
        axios.get(`${API_URL}/pa/statuts`).catch(() => ({ data: null })),
        axios.get(`${API_URL}/ereporting/dashboard`).catch(() => ({ data: null })),
      ]);
      setDashboard(dashRes.data);
      setConfig(configRes.data);
      setStatuts(statutsRes.data);
      setEreportingDash(erDash.data);
      if (configRes.data) {
        setConfigForm({
          is_active: configRes.data.is_active || false,
          pdp_provider: configRes.data.pdp_provider || '',
          pdp_api_url: configRes.data.pdp_api_url || '',
          pdp_api_key: '',
          siren: configRes.data.siren || '',
          format_prefere: configRes.data.format_prefere || 'factur-x',
          environment: configRes.data.environment || 'sandbox',
        });
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadTransactions = async () => {
    try {
      const params = {};
      if (directionFilter !== 'all') params.direction = directionFilter;
      if (statutFilter !== 'all') params.statut = statutFilter;
      const res = await axios.get(`${API_URL}/pa/transactions`, { params });
      setTransactions(res.data);
    } catch (e) { toast.error('Erreur chargement transactions'); }
  };

  const handleAnnuaireLookup = async () => {
    if (!annuaireSearch || annuaireSearch.length < 9) { toast.error('Saisissez un SIREN (9 chiffres) ou SIRET (14 chiffres)'); return; }
    setAnnuaireLoading(true);
    try {
      const res = await axios.post(`${API_URL}/pa/annuaire/lookup`, { siren_or_siret: annuaireSearch });
      setAnnuaireResult(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setAnnuaireLoading(false); }
  };

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try {
      await axios.put(`${API_URL}/pa/config`, configForm);
      toast.success('Configuration sauvegardée');
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSavingConfig(false); }
  };

  const handleTestConnection = async () => {
    try {
      const res = await axios.post(`${API_URL}/pa/config/test`);
      if (res.data.success) toast.success(res.data.message);
      else toast.error(res.data.message);
    } catch (e) { toast.error('Erreur test connexion'); }
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-[#0F5156]" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white"><Zap className="w-6 h-6 inline mr-2 text-blue-600" />Facturation Électronique</h1>
          <p className="text-slate-500">Réforme sept. 2026 — Plateforme Agréée, Annuaire PPF, E-Reporting</p>
        </div>
        {config?.is_configured ? (
          <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"><CheckCircle className="w-3 h-3 mr-1" />PA Configurée ({config.pdp_provider})</Badge>
        ) : (
          <Badge className="bg-amber-100 text-amber-700"><Settings className="w-3 h-3 mr-1" />Configuration requise</Badge>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
          <TabsTrigger value="annuaire">Annuaire PPF</TabsTrigger>
          <TabsTrigger value="ereporting">E-Reporting</TabsTrigger>
          <TabsTrigger value="config">Configuration PA</TabsTrigger>
        </TabsList>

        {/* ==================== DASHBOARD ==================== */}
        <TabsContent value="dashboard" className="space-y-6">
          <div className="grid grid-cols-4 gap-4">
            <Card><CardContent className="pt-5 text-center">
              <ArrowUpRight className="w-5 h-5 mx-auto text-blue-600 mb-1" />
              <p className="text-2xl font-bold">{dashboard?.envoi?.total || 0}</p>
              <p className="text-xs text-slate-500">Factures émises</p>
            </CardContent></Card>
            <Card><CardContent className="pt-5 text-center">
              <ArrowDownLeft className="w-5 h-5 mx-auto text-purple-600 mb-1" />
              <p className="text-2xl font-bold">{dashboard?.reception?.total || 0}</p>
              <p className="text-xs text-slate-500">Factures reçues</p>
            </CardContent></Card>
            <Card><CardContent className="pt-5 text-center">
              <CheckCircle className="w-5 h-5 mx-auto text-green-600 mb-1" />
              <p className="text-2xl font-bold">{(dashboard?.envoi?.par_statut?.deposee || 0) + (dashboard?.envoi?.par_statut?.encaissee || 0)}</p>
              <p className="text-xs text-slate-500">Déposées / Encaissées</p>
            </CardContent></Card>
            <Card><CardContent className="pt-5 text-center">
              <Clock className="w-5 h-5 mx-auto text-amber-600 mb-1" />
              <p className="text-2xl font-bold">{ereportingDash?.a_transmettre || 0}</p>
              <p className="text-xs text-slate-500">E-Reports en attente</p>
            </CardContent></Card>
          </div>

          {/* Statuts émission */}
          {dashboard?.envoi?.par_statut && Object.keys(dashboard.envoi.par_statut).length > 0 && (
            <Card>
              <CardContent className="pt-5">
                <h3 className="font-semibold mb-3 flex items-center gap-2"><ArrowUpRight className="w-4 h-4 text-blue-600" />Émission — Statuts du cycle de vie</h3>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(dashboard.envoi.par_statut).map(([statut, count]) => (
                    <Badge key={statut} className={getStatutFiscalColor(statut)}>{formatStatutFiscal(statut)} : {count}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Statuts réception */}
          {dashboard?.reception?.par_statut && Object.keys(dashboard.reception.par_statut).length > 0 && (
            <Card>
              <CardContent className="pt-5">
                <h3 className="font-semibold mb-3 flex items-center gap-2"><ArrowDownLeft className="w-4 h-4 text-purple-600" />Réception — Statuts du cycle de vie</h3>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(dashboard.reception.par_statut).map(([statut, count]) => (
                    <Badge key={statut} className={getStatutFiscalColor(statut)}>{formatStatutFiscal(statut)} : {count}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Référentiel statuts */}
          {statuts && (
            <Card>
              <CardContent className="pt-5">
                <h3 className="font-semibold mb-3 flex items-center gap-2"><Shield className="w-4 h-4" />Référentiel des statuts réglementaires</h3>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="font-medium text-red-700 mb-2">Obligatoires (transmis au PPF)</p>
                    {statuts.obligatoires?.map(s => (
                      <div key={s.code} className="flex items-center gap-2 mb-1">
                        <Badge className={getStatutFiscalColor(s.code) + ' text-xs'}>{s.label}</Badge>
                        {s.terminal && <span className="text-xs text-red-500">(terminal)</span>}
                      </div>
                    ))}
                  </div>
                  <div>
                    <p className="font-medium text-blue-700 mb-2">Recommandés</p>
                    {statuts.recommandes?.map(s => (
                      <div key={s.code} className="mb-1"><Badge className={getStatutFiscalColor(s.code) + ' text-xs'}>{s.label}</Badge></div>
                    ))}
                  </div>
                  <div>
                    <p className="font-medium text-slate-600 mb-2">Libres (internes)</p>
                    {statuts.libres?.map(s => (
                      <div key={s.code} className="mb-1"><Badge className={getStatutFiscalColor(s.code) + ' text-xs'}>{s.label}</Badge></div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ==================== TRANSACTIONS ==================== */}
        <TabsContent value="transactions" className="space-y-4">
          <div className="flex gap-4">
            <Select value={directionFilter} onValueChange={v => setDirectionFilter(v)}>
              <SelectTrigger className="w-48"><SelectValue placeholder="Direction" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                <SelectItem value="envoi">Émises</SelectItem>
                <SelectItem value="reception">Reçues</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statutFilter} onValueChange={v => setStatutFilter(v)}>
              <SelectTrigger className="w-48"><SelectValue placeholder="Statut" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="deposee">Déposée</SelectItem>
                <SelectItem value="mise_a_disposition">Mise à disposition</SelectItem>
                <SelectItem value="approuvee">Approuvée</SelectItem>
                <SelectItem value="refusee">Refusée</SelectItem>
                <SelectItem value="encaissee">Encaissée</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={loadTransactions}><RefreshCw className="w-4 h-4 mr-2" />Rafraîchir</Button>
          </div>

          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead className="w-12"></TableHead>
                <TableHead>Facture</TableHead>
                <TableHead>Format</TableHead>
                <TableHead>SIREN Émetteur</TableHead>
                <TableHead>SIREN Destinataire</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Réf. PA</TableHead>
                <TableHead>Date</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {transactions.map(t => (
                  <TableRow key={t.id}>
                    <TableCell>{t.direction === 'envoi' ? <ArrowUpRight className="w-4 h-4 text-blue-600" /> : <ArrowDownLeft className="w-4 h-4 text-purple-600" />}</TableCell>
                    <TableCell className="font-mono text-sm font-medium">{t.facture_numero || '—'}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{FORMAT_EINVOICE_LABELS[t.format] || t.format}</Badge></TableCell>
                    <TableCell className="font-mono text-sm">{t.siren_emetteur || '—'}</TableCell>
                    <TableCell className="font-mono text-sm">{t.siren_destinataire || '—'}</TableCell>
                    <TableCell><Badge className={getStatutFiscalColor(t.statut)}>{formatStatutFiscal(t.statut)}</Badge></TableCell>
                    <TableCell className="font-mono text-xs">{t.pdp_reference || '—'}</TableCell>
                    <TableCell className="text-sm">{formatDateShort(t.created_at)}</TableCell>
                  </TableRow>
                ))}
                {transactions.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-12 text-slate-500">Aucune transaction e-invoicing</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        {/* ==================== ANNUAIRE PPF ==================== */}
        <TabsContent value="annuaire" className="space-y-4">
          <Card><CardContent className="pt-5">
            <h3 className="font-semibold mb-3 flex items-center gap-2"><Globe className="w-4 h-4 text-blue-600" />Interroger l'annuaire PPF</h3>
            <p className="text-sm text-slate-500 mb-4">Recherchez un destinataire par SIREN ou SIRET pour trouver sa Plateforme Agréée et ses informations de routage.</p>
            <div className="flex gap-3">
              <Input placeholder="SIREN (9 chiffres) ou SIRET (14 chiffres)" value={annuaireSearch} onChange={e => setAnnuaireSearch(e.target.value)} className="max-w-sm" />
              <Button onClick={handleAnnuaireLookup} disabled={annuaireLoading} className="bg-[#0F5156] hover:bg-[#0d464a]">
                {annuaireLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}Rechercher
              </Button>
            </div>

            {annuaireResult && (
              <div className="mt-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                {annuaireResult.found ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-green-600" /><span className="font-semibold text-green-700">Destinataire trouvé</span></div>
                    <div className="grid grid-cols-2 gap-3 text-sm mt-3">
                      <div><span className="text-slate-500">SIREN :</span> <span className="font-mono font-medium">{annuaireResult.siren}</span></div>
                      <div><span className="text-slate-500">Dénomination :</span> <span className="font-medium">{annuaireResult.denomination}</span></div>
                      <div><span className="text-slate-500">Plateforme Agréée :</span> <Badge className="bg-blue-100 text-blue-700">{annuaireResult.plateforme_agreee_nom}</Badge></div>
                      <div><span className="text-slate-500">ID PA :</span> <span className="font-mono text-xs">{annuaireResult.plateforme_agreee_id}</span></div>
                      <div><span className="text-slate-500">Maille :</span> <span>{annuaireResult.maille_reception === 'siren' ? 'SIREN (siège social)' : 'SIRET (établissement)'}</span></div>
                      <div><span className="text-slate-500">Code routage :</span> <span className="font-mono text-xs">{annuaireResult.code_routage}</span></div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2"><XCircle className="w-5 h-5 text-red-500" /><span className="text-red-600">{annuaireResult.error || 'Destinataire non trouvé dans l\'annuaire PPF'}</span></div>
                )}
              </div>
            )}
          </CardContent></Card>
        </TabsContent>

        {/* ==================== E-REPORTING ==================== */}
        <TabsContent value="ereporting" className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <Card className="border-blue-200 dark:border-blue-800"><CardContent className="pt-5">
              <h4 className="font-semibold text-sm mb-2">B2C (Particuliers)</h4>
              <p className="text-xs text-slate-500 mb-3">Données agrégées quotidiennement</p>
              <div className="text-sm space-y-1">
                <div className="flex justify-between"><span>À transmettre :</span><Badge variant="outline">{ereportingDash?.par_type?.b2c_agregee?.a_transmettre?.count || 0}</Badge></div>
                <div className="flex justify-between"><span>Transmis :</span><Badge className="bg-green-100 text-green-700">{ereportingDash?.par_type?.b2c_agregee?.transmis?.count || 0}</Badge></div>
              </div>
            </CardContent></Card>
            <Card className="border-purple-200 dark:border-purple-800"><CardContent className="pt-5">
              <h4 className="font-semibold text-sm mb-2">International</h4>
              <p className="text-xs text-slate-500 mb-3">Export, intra-UE, autoliquidation</p>
              <div className="text-sm space-y-1">
                <div className="flex justify-between"><span>À transmettre :</span><Badge variant="outline">{ereportingDash?.par_type?.international?.a_transmettre?.count || 0}</Badge></div>
                <div className="flex justify-between"><span>Transmis :</span><Badge className="bg-green-100 text-green-700">{ereportingDash?.par_type?.international?.transmis?.count || 0}</Badge></div>
              </div>
            </CardContent></Card>
            <Card className="border-amber-200 dark:border-amber-800"><CardContent className="pt-5">
              <h4 className="font-semibold text-sm mb-2">Encaissements</h4>
              <p className="text-xs text-slate-500 mb-3">Obligatoire si TVA sur encaissements</p>
              <div className="text-sm space-y-1">
                <div className="flex justify-between"><span>À transmettre :</span><Badge variant="outline">{ereportingDash?.par_type?.encaissement?.a_transmettre?.count || 0}</Badge></div>
                <div className="flex justify-between"><span>Transmis :</span><Badge className="bg-green-100 text-green-700">{ereportingDash?.par_type?.encaissement?.transmis?.count || 0}</Badge></div>
              </div>
            </CardContent></Card>
          </div>
        </TabsContent>

        {/* ==================== CONFIGURATION PA ==================== */}
        <TabsContent value="config" className="space-y-4">
          <Card><CardContent className="pt-5 space-y-4">
            <h3 className="font-semibold flex items-center gap-2"><Settings className="w-4 h-4" />Configuration Plateforme Agréée</h3>
            <p className="text-sm text-slate-500">Configurez la connexion à votre Plateforme Agréée (PA) pour l'émission et la réception des factures électroniques.</p>

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 flex items-center gap-3">
                <Label>Activer l'e-invoicing</Label>
                <input type="checkbox" checked={configForm.is_active} onChange={e => setConfigForm({...configForm, is_active: e.target.checked})} className="w-4 h-4" />
              </div>
              <div><Label>Fournisseur PA</Label>
                <Select value={configForm.pdp_provider} onValueChange={v => setConfigForm({...configForm, pdp_provider: v})}>
                  <SelectTrigger><SelectValue placeholder="Choisir..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="chorus">Chorus Pro</SelectItem>
                    <SelectItem value="pennylane">Pennylane</SelectItem>
                    <SelectItem value="cegid">Cegid</SelectItem>
                    <SelectItem value="sage">Sage</SelectItem>
                    <SelectItem value="dematrust">DematrUST</SelectItem>
                    <SelectItem value="docaposte">Docaposte</SelectItem>
                    <SelectItem value="edi_piste">EDI / PISTE</SelectItem>
                    <SelectItem value="autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Environnement</Label>
                <Select value={configForm.environment} onValueChange={v => setConfigForm({...configForm, environment: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sandbox">Sandbox (test)</SelectItem>
                    <SelectItem value="production">Production</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>URL API de la PA</Label><Input value={configForm.pdp_api_url} onChange={e => setConfigForm({...configForm, pdp_api_url: e.target.value})} placeholder="https://api.pa-provider.fr/v1" /></div>
              <div><Label>Clé API</Label><Input type="password" value={configForm.pdp_api_key} onChange={e => setConfigForm({...configForm, pdp_api_key: e.target.value})} placeholder="Laisser vide pour conserver" /></div>
              <div><Label>SIREN de la structure</Label><Input value={configForm.siren} onChange={e => setConfigForm({...configForm, siren: e.target.value})} placeholder="123456789" /></div>
              <div><Label>Format préféré</Label>
                <Select value={configForm.format_prefere} onValueChange={v => setConfigForm({...configForm, format_prefere: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="factur-x">Factur-X (PDF + XML)</SelectItem>
                    <SelectItem value="ubl">UBL 2.1 (XML)</SelectItem>
                    <SelectItem value="cii">CII (XML)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button onClick={handleSaveConfig} disabled={savingConfig} className="bg-[#0F5156] hover:bg-[#0d464a]">
                {savingConfig && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Sauvegarder
              </Button>
              <Button variant="outline" onClick={handleTestConnection}>Tester la connexion</Button>
            </div>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
